import { PositionService } from './position.service';
import { ProjectService } from './project.service';
import { StatMetaData } from './../models/stat-meta-data';
import { MotesLoginService } from './../motes-login/motes-login.service';
import { inject, Injectable } from '@angular/core';
import { first, tap, switchMap, map, filter } from 'rxjs/operators';
import { DeviceDetectorService } from 'ngx-device-detector';
import { IpInfo, ServiceId, StatEventBase } from '../models';
import { of, Observable, combineLatest, Subject, ReplaySubject, firstValueFrom } from 'rxjs';
import { UserRights } from '../motes-login/motes-login.models';
import { StatEvent, StatType, StatEventJson, StatEventUserCreated } from '../models/stat-event';
import { DatabaseService } from './database.service';
import { HttpClient } from '@angular/common/http';

import { DeviceService } from './device.service';
import { LocalStorage } from 'ngx-webstorage';
import { addDoc, collection, collectionData, CollectionReference, deleteDoc, doc, endAt, Firestore, orderBy, Query, query, startAt, Timestamp, updateDoc, where } from '@angular/fire/firestore';
import { MotesCookieService } from '@motes-sdk/cookie/cookie.service';

export enum HourRangeType {
    NONE = "NONE",
    AUTO = "AUTO",
    HOUR = "HOUR",
    DAY = "DAY",
    WEEK = "WEEK",
    MONTH = "MONTH",
    YEAR = "YEAR"
}

export class TimeSlots {
    slots: { [slotId: string]: string } = {};
    labels: { [slotId: string]: string } = {};
    hourRange: number;
    hourRangeType: HourRangeType;
}

@Injectable({ providedIn: 'root' })
export class StatsService {
    // Get / save device key in local storage
    @LocalStorage('deviceKey', '') private deviceKey: string;
    @LocalStorage('visitCount', 0) private visitCount;

    private chunkCache: { [chunkId: number]: StatEvent[] } = {};
    private initSubject = new ReplaySubject<void>(1);

    private VISIT_DB_PREFIX = "visits/";
    private visitPath = this.VISIT_DB_PREFIX + "InvalidVisitPath";
    private loginPath = this.VISIT_DB_PREFIX + "InvalidLoginPath";

    public currentVisitKey: string = null;
    private metaDataSubject: Subject<StatMetaData> = new ReplaySubject(1);
    // Expose last meta data (use awaitMetaData if you wanna be sure metadata is already fetched)
    public lastMetaData: StatMetaData = null;

    private isProduction: boolean = true;
    private redirectKeyword: string = 'unknown';

    public visitedProjects: { [key: string]: any } = {};
    public serviceId: ServiceId;

    private activeProjectCacheKey = "";

    constructor(private http: HttpClient,
        private af: DatabaseService,
        private db: Firestore = inject(Firestore),
        private loginService: MotesLoginService,
        private projectService: ProjectService,
        private positionService: PositionService,
        private deviceService: DeviceService,
        private cookieService: MotesCookieService,
        private deviceDetectorService: DeviceDetectorService) {
    }

    /**
    * Initialize login service (save redirect keyword etc.)
    */
    public init(serviceId: ServiceId, keyword: string = 'direct', isProduction: boolean = true): void {
        this.redirectKeyword = keyword;
        this.isProduction = isProduction;
        this.serviceId = serviceId;
        this.initIpMetaData(isProduction);
        this.initSubject.next();
    }

    /**
    * Watch user rights, increment user visit count and add visits (logins) to database on each successful login
    * (should only be called once per application load)
    */
    public enableUserVisitStats(): Observable<UserRights | null> {
        // Generate a new visit key for this session (no matter if the user has any user rights or not)
        if(!this.currentVisitKey) this.currentVisitKey = this.generateKey();
        // Increment visit counter
        this.incrementVisitCount();

        let watchUserRights = this.loginService.watchUserRights().pipe(tap(async userRights => {
            if(!userRights) return;
            this.initVisitPath(userRights);
            // Add user visit to database for users with user rights (anonymous or not)
            await this.addUserVisit(userRights.uid);
        }));

        let watchUserVisits = combineLatest(watchUserRights, this.watchProjectVisits()).pipe(map(userVisited => {
            // Save user visited projects in stats service
            this.visitedProjects = userVisited[1] || {};
            return userVisited[0];
        }));

        return watchUserVisits;
    }

    /**
    * Save a new visit with user meta data in current database session
    */
    private async addUserVisit(userKey: string): Promise<void> {
        if(!userKey) {
            console.error("NO USERKEY!");
            return;
        }
        // Await ip address meta data
        const metaData = await this.awaitMetaData();
        // Save visit and IP info in database
        await this.af.object(this.loginPath + this.currentVisitKey).update(metaData);
        // Save end time and offline state on disconnect
        let ref = this.af.database.ref(this.loginPath + this.currentVisitKey);
        // Update user session end timestamp when client disconnects from firebase server
        await ref.onDisconnect().update({ endTime: Timestamp.now(), online: false });
    }

    private initVisitPath(userRights: UserRights) {
        let userKey = userRights ? userRights.uid : "noUserKey";
        // Update user stats with new projects and timestamps
        if(this.serviceId == ServiceId.STUDIO) {
            this.visitPath = this.VISIT_DB_PREFIX + userKey + '/proProjectsVisited';
            this.loginPath = this.VISIT_DB_PREFIX + userKey + '/proLogin/';
        }
        else if(this.serviceId == ServiceId.CREATOR) {
            this.visitPath = this.VISIT_DB_PREFIX + userKey + '/creatorVisited';
            this.loginPath = this.VISIT_DB_PREFIX + userKey + '/creatorLogin/';
        }
        else {
            this.visitPath = this.VISIT_DB_PREFIX + userKey + '/projectsVisited';
            this.loginPath = this.VISIT_DB_PREFIX + userKey + '/appLogin/';
        }
    }

    /**
     * Init request of meta data from ip info server (ipinfo.io) and distribute it to listeners (via metaDataSubject)
     */
     private async initIpMetaData(isProduction: boolean, abVersion: string = '') {
        // Get meta Data form ip server
        let metaData = await this.requestIpMetaData(isProduction, abVersion);
        // Notify listeners of new meta data
        this.metaDataSubject.next(metaData);
    }

    /**
    * Get / wait for cached ip server meta data (initIpMetaData must be called in order to not wait forever)
    */
    private awaitMetaData(): Promise<StatMetaData> {
        return this.metaDataSubject.pipe(first()).toPromise();
    }

    /**
    * Mark the given project as visited by this user (save it in database)
    */
    public addProjectVisited(projectKey: string): { [key: string]: any } {
        if(this.visitedProjects[projectKey]) return this.visitedProjects;
        // Generate project object
        this.visitedProjects[projectKey] = Timestamp.now();
        // Update user stats with new projects and timestamps
        //console.log("Update projects visited " +  this.visitPath, this.visitedProjects);
        this.af.object(this.visitPath).update(this.visitedProjects);
        return this.visitedProjects;
    }

    /**
    * Return map with current users projects visited
    */
    public watchProjectVisits(): Observable<{ [key: string]: any }> {
        let servicePath = (this.serviceId == ServiceId.STUDIO) ? '/proProjectsVisited' : '/projectsVisited/';

        return this.loginService.watchUserRights().pipe(filter(rights => rights != null), switchMap(rights => {
            if(!rights) return of({});
            return this.af.object(this.VISIT_DB_PREFIX + rights.uid + servicePath).valueChanges();
        }));
    }

    private requestIpMetaData(isProduction: boolean = false, abVersion: string = 'unset'): Promise<StatMetaData> {
        // Get metaData from ipInfo (max 1000 requests / day)
        const ipInfoAddress: string = 'https://ipinfo.io/?token=833367817642ce';
        return new Promise((resolve, reject) => {
            this.http.get<IpInfo>(ipInfoAddress).pipe(first()).subscribe((ipJson) => {

                // Unpack response
                let positionArray = ipJson.loc.split(',');
                let lat = positionArray[0];
                let long = positionArray[1];

                // Create metaData object
                let metaData: StatMetaData = {
                    online: true,
                    abTest: abVersion,
                    ip: ipJson.ip,
                    lat: parseFloat(lat),
                    long: parseFloat(long),
                    production: isProduction,
                    referrer: (typeof document == 'undefined') ? null : document.referrer,
                    country: ipJson.country,
                    city: ipJson.city,
                    deviceInfo: this.deviceDetectorService.getDeviceInfo(),
                    ipInfo: ipJson,
                    keyword: this.redirectKeyword,
                    startTime: Timestamp.now()
                };
                this.lastMetaData = metaData;
                resolve(metaData);
            });
        });
    }

    public generateKey() {
        return this.af.database.ref().push().key;
    }

    /**
     * Get device key from local storage or generate a new one if it doesn't exist on client yet
     */
    public getDeviceKey() {
        if(!this.deviceKey) this.deviceKey = this.generateKey();
        return this.deviceKey;
    }

    /**
    * Add a event to the event stats list
    */
    public async addStatsEvent(type: StatType, mote: {key: string, project: string, parent: string} = null, addition: any = null): Promise<string> {
        // Don't send stat events during ssr
        if(this.deviceService.isPlatformServer()) return;
        // Get current user
        let userRights = await this.loginService.getUserCurrentRights();

        // Wait for active project in App - don't wait in Home and Creator
        const getActiveProject = (!mote?.project && this.serviceId != ServiceId.HOME && this.serviceId != ServiceId.CREATOR);
        let activeProject = (getActiveProject) ? await this.projectService.getActiveProject() : null;

        // Get server timestamp of active visit
        let startTime = await this.af.object(this.loginPath + this.currentVisitKey + "/startTime").valueChanges().pipe(first()).toPromise();
        let metaData = await this.awaitMetaData();
        let visitCount = this.getVisitCount();

        if(!this.currentVisitKey) this.currentVisitKey = this.generateKey();

        if(!metaData) {
            console.error("No  meta data, add stats event without", metaData);
        }

        let pos = this.positionService.lastKnownPosition;

        const visitorKey = await this.cookieService.getVisitorKey();

        // Create generic stat json
        let json: StatEventJson<number> = {
            statType: type,
            // Set created on to null to set server timestamp
            createdOn: null,
            visitKey: this.currentVisitKey,
            visitorKey: visitorKey,
            deviceKey: this.getDeviceKey(),
            visitCount: visitCount,
            visitStartTime: startTime as number,
            projectKey: mote?.project || activeProject?.key,
            position: pos.isValid ? pos.toJson() : { lat: metaData?.lat || 0, long: metaData?.long || 0},
            moteKey: mote?.key || null,
            tourKey: mote?.parent || activeProject?.activeRouteKey || null,
            isAdmin: userRights && userRights.isAdmin || userRights && userRights.isSuperAdmin,
            addition: addition || null,
            serviceId: this.serviceId || null,
            referer: encodeURIComponent(document?.referrer || '') 
        }
        // Allocate generic user stat event or special user created 
        let ev = (type == StatType.USER_CREATED) ? new StatEventUserCreated(json) : new StatEventBase(json) as StatEvent;

        // Await stats init before trying to save stat event in order to avoid firestore to fail silently (when there is no user etc.)
        await firstValueFrom(this.initSubject);

        // Add additional parameters to user created event
        if(ev.statType == StatType.USER_CREATED && metaData) {
            ev.country = (metaData.country) ? metaData.country : null;
            ev.city = (metaData.city) ? metaData.city : null;
            ev.language = navigator.language;
            ev.browser = metaData.deviceInfo.os + "_" + metaData.deviceInfo.browser;
            ev.device = metaData.deviceInfo.device;
            ev.userAgent = metaData.deviceInfo.userAgent;
        }

        // Obs: Do not save stat events on dev servers
        if(this.deviceService.isDevServerDeploy()) return;

        // Save event in database
        let stat = await addDoc(this.getStatsCollection(), ev.toJson()).catch( err => {
            // This seems to never trigger even when firebase falls saving (hangs forever), however keep the catch here in case it is fixed
            console.log("Could not save stat event", err);
            return { id: 'error_not_saved'};
        });
        return stat.id;
    }

    public changeStatEventType(ev: StatEvent, statType: StatType) {
        // Check id
        if(!ev.id) {
            console.error("Could not change stat event type ", ev);
            return;
        }

        // Update stat event type
        return updateDoc(doc(this.getStatsCollection(), ev.id), {
            statType: statType
        });
    }

    public deleteStatEvent(ev: StatEvent) {
        // Check id
        if(!ev.id) {
            console.error("Could not delete stat event type ", ev);
            return;
        }
        // Update stat event type
        return deleteDoc(doc(this.getStatsCollection(), ev.id));
    }

    /**
     * Add a event to database with additional info (the rest of the event body will automatically populated)
     * @param StatEvent 
     */
    public addStatsEventAddition(stat: StatEventJson<Date>, mote?: {key: string, project: string, parent: string}): Promise<string> {
        return this.addStatsEvent(stat.statType, mote, stat.addition);
    }

    public watchStatsCache(startTime: number, dayChunkSize: number, projectKey: string): Observable<StatEvent[]> {
        if(this.activeProjectCacheKey != projectKey) {
            // Clear chunk cache
            this.chunkCache = {};
            this.activeProjectCacheKey = projectKey;
        }
        // Return cached chunk
        if(this.chunkCache[startTime]) {
            return of(this.chunkCache[startTime]);
        }

        // Get a fresh chunk
        let endTime = startTime + dayChunkSize * 24 * 60 * 60 * 1000;
        // Return caching version of stats observable
        return this.watchStats(startTime, endTime, projectKey).pipe(tap(events => this.chunkCache[startTime] = events));
    }

    /**
    * Watch stats based on project within the given timeperiod
    * @param startTime
    * @param endTime
    * @param projectKey
    */
    public watchStats(startTime: number, endTime: number, projectKey: string): Observable<StatEvent[]> {
        let startDate = new Date(startTime);
        let endDate = new Date(endTime);

        let stats = query(this.getStatsCollection(), orderBy("createdOn"), startAt(startDate), endAt(endDate), where("projectKey", "==", projectKey));

        return collectionData(stats, { idField: 'id' }).pipe(map(actions => {
            let events = actions.map(action => {
                const json = action as StatEventJson<number>;
                return (json.statType == StatType.USER_CREATED) ? new StatEventUserCreated(json) : new StatEvent(json);
            });
            return events || [];
        }));
    }

    public getStatsCache(chunkStartTimes: number[]): StatEvent[] {
        // Return cached chunk
        let events = [];
        for(let chunk of chunkStartTimes) {
            if(this.chunkCache[chunk]) {
                events.push(...this.chunkCache[chunk]);
            }
        }
        return events;
    }

    /**
    * Watch all stats in the given time period of a specific type
    * @param startTime
    * @param endTime
    * @param projectKey
    */
    public watchStatType(startTime: number, endTime: number, statType: StatType, projectKey = ""): Observable<StatEvent[]> {
        let startDate = new Date(startTime);
        let endDate = new Date(endTime);
        let stats: Query;

        if(projectKey) {
            stats = query(this.getStatsCollection(), orderBy("createdOn"), startAt(startDate), endAt(endDate), where("statType", "==", statType), where("projectKey", "==", projectKey));
        }
        else {
            stats =  query(this.getStatsCollection(), orderBy("createdOn"), startAt(startDate), endAt(endDate), where("statType", "==", statType));
        }

        return collectionData(stats).pipe(map(actions => {
            let events = actions.map(action => {
                let json = action as StatEventJson<number>;
                return (json.statType == StatType.USER_CREATED) ? new StatEventUserCreated(json) : new StatEvent(json);
            });
            return events || [];
        }));
    }

    /**
    * Get visit count from user device (not user profile)
    */
    public getVisitCount(): number {
        return this.visitCount;
    }

    /**
     * Set visit count on device
     */
    public setVisitCount(visitCount: number): void {
        // Persist visit count in local storage
        this.visitCount = visitCount;
    }

    /**
     * Increment
     */
    public incrementVisitCount(): void {
        let visitCount = this.getVisitCount();
        this.setVisitCount(++visitCount);
    }

    private getStatsCollection() : CollectionReference {
        return collection(this.db, "stats");
    }
}
